import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Sparkles, Shield, Mail, LogOut, Crown, Lock } from "lucide-react";
import { startCheckout, openCustomerPortal } from "@/lib/billing";
import { toast } from "sonner";

export default function Account() {
  const { user, isPro, isAdmin, signOut } = useAuth();
  return (
    <div className="container py-10 max-w-2xl">
      <h1 className="font-display text-4xl font-bold">Account</h1>
      <p className="text-muted-foreground mt-1">Manage your profile and subscription.</p>

      {/* Profile */}
      <div className="te-card p-6 mt-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-primary grid place-items-center text-primary-foreground font-display text-lg font-bold shadow-glow">
            {(user?.email ?? "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase text-muted-foreground tracking-wider flex items-center gap-1.5"><Mail className="w-3 h-3" /> Email</div>
            <div className="font-medium truncate">{user?.email}</div>
          </div>
          {isAdmin && <span className="te-chip bg-accent/15 text-accent border border-accent/30 shrink-0"><Shield className="w-3 h-3" /> Admin</span>}
        </div>
      </div>

      {/* Subscription */}
      <div className={`te-card p-6 mt-4 relative overflow-hidden ${isPro ? "ring-1 ring-primary/40" : ""}`}>
        {isPro && <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-gradient-primary opacity-15 blur-3xl pointer-events-none" />}
        <div className="text-xs uppercase text-muted-foreground tracking-wider flex items-center gap-1.5">
          {isPro ? <Crown className="w-3 h-3 text-accent" /> : <Lock className="w-3 h-3" />} Subscription
        </div>
        <div className="flex items-center justify-between mt-2 gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="font-display text-3xl font-bold flex items-center gap-2">
              {isPro ? <span className="te-grad-text">Pro</span> : "Free"}
              {isPro && <span className="te-chip bg-primary/15 text-primary border border-primary/30 text-[10px]">Active</span>}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isPro ? "Full access to Top 5, breakdowns, and the parlay of the day." : "1 free daily pick included."}
            </p>
          </div>
          {!isAdmin && !isPro && (
            <Button className="bg-gradient-accent text-accent-foreground hover:opacity-90 shadow-glow" onClick={async () => { try { await startCheckout(); } catch (e: any) { toast.error(e?.message ?? "Unable to start checkout"); } }}><Sparkles className="w-4 h-4 mr-2" />Upgrade to Pro</Button>
          )}
          {!isAdmin && isPro && (
            <Button variant="secondary" onClick={async () => { try { await openCustomerPortal(); } catch (e: any) { toast.error(e?.message ?? "Unable to open billing portal"); } }}>Manage Billing</Button>
          )}
        </div>

        {!isAdmin && !isPro && (
          <div className="mt-5 grid sm:grid-cols-3 gap-2 text-xs">
            <Perk title="Top 5 picks" />
            <Perk title="Parlay of the Day" />
            <Perk title="Full breakdowns" />
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <Button variant="ghost" onClick={signOut}><LogOut className="w-4 h-4 mr-2" /> Sign out</Button>
      </div>
    </div>
  );
}

function Perk({ title }: { title: string }) {
  return (
    <div className="rounded-xl bg-secondary/50 border border-border/60 px-3 py-2 flex items-center gap-2">
      <Sparkles className="w-3 h-3 text-accent" />
      <span>{title}</span>
    </div>
  );
}
